import React, { useState, useEffect } from "react";
// Added Download icon
import { ArrowLeft, Upload, Check, AlertCircle, Download } from "lucide-react";
import { Button } from "components/ui/button";
import {
  getEquipmentCategories,
  previewEquipmentCsv,
  bulkSaveEquipment,
} from "services/equipmentService";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "components/ui/select";

const BulkUploadEquipment = ({ onBack, onComplete }) => {
  const [step, setStep] = useState(1);
  const [file, setFile] = useState(null);
  const [categories, setCategories] = useState([]);
  const [previewData, setPreviewData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleDownloadTemplate = () => {
    const templatePath = "/templates/equipment_template.csv";
    const link = document.createElement("a");
    link.href = templatePath;
    link.setAttribute("download", "equipment_upload_template.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  useEffect(() => {
    const fetchCats = async () => {
      try {
        const res = await getEquipmentCategories();
        let finalData = [];
        if (Array.isArray(res)) {
          finalData = res;
        } else if (res?.data && Array.isArray(res.data)) {
          finalData = res.data;
        }
        setCategories(finalData);
      } catch (err) {
        console.error("Failed to fetch categories", err);
      }
    };
    fetchCats();
  }, []);

  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
    setError(null);
  };

  const handleUploadPreview = async () => {
    if (!file) return;
    setLoading(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append("file", file);

      const res = await previewEquipmentCsv(formData);

      let items = [];
      if (res && res.error === false && Array.isArray(res.data)) {
        items = res.data;
      } else if (Array.isArray(res)) {
        items = res;
      }
      if (items.length > 0) {
        const initialData = items.map((item) => ({
          equipment_name: item.equipment_name || "",
          manufacturer: item.manufacturer || "",
          model_number: item.model_number || "",
          serial_number: item.serial_number || "",
          purchase_price: item.purchase_price || "",
          daily_rental_rate: item.daily_rental_rate || "",
          storage_location: item.storage_location || "",
          description: item.description || "",
          organization_type: item.organization_type || "1",
          initial_status_id: item.initial_status_id || "1",
          category_id: "",
        }));
        setPreviewData(initialData);
        setStep(2);
      } else {
        setError(res?.message || "The CSV file appears to be empty or in the wrong format.");
      }
    } catch (err) {
      setError("Error uploading file. Please check server logs.");
    } finally {
      setLoading(false);
    }
  };

  const handleCategoryChange = (index, categoryId) => {
    const newData = [...previewData];
    newData[index].category_id = categoryId;
    setPreviewData(newData);
  };

  const handleFinalSave = async () => {
    if (previewData.some((item) => !item.category_id)) {
      setError("Please select a category for all items.");
      return;
    }
    setLoading(true);
    try {
      const res = await bulkSaveEquipment({ equipment_list: previewData });
      if (!res.error) onComplete();
      else setError(res.message || "Failed to save equipment.");
    } catch (err) {
      setError("Server error during save.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 max-w-[1600px] mx-auto">
      {/* Updated Header with Download Button */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={onBack} size="icon">
            <ArrowLeft size={20} />
          </Button>
          <div>
            <h1 className="text-2xl font-bold">Bulk Upload Equipment</h1>
            <p className="text-muted-foreground">Import multiple items via CSV</p>
          </div>
        </div>

        <Button
          variant="outline"
          onClick={handleDownloadTemplate}
          className="hidden md:flex items-center gap-2 border-primary text-primary hover:bg-primary/5"
        >
          <Download size={16} />
          Download Template
        </Button>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-600 rounded-lg flex items-center gap-2">
          <AlertCircle size={18} /> {error}
        </div>
      )}

      {step === 1 ? (
        <div className="bg-card border-2 border-dashed border-border rounded-xl p-12 text-center">
          <Upload className="mx-auto w-12 h-12 text-muted-foreground mb-4" />
          <h2 className="text-lg font-semibold mb-2">Select CSV File</h2>

          {/* New informative text with download link */}
          <p className="text-sm text-muted-foreground mb-6">
            Please use our official template to ensure data compatibility.<br/>
            <button
              onClick={handleDownloadTemplate}
              className="text-primary font-medium hover:underline inline-flex items-center gap-1 mt-1"
            >
              Download equipment_template.csv
            </button>
          </p>

          <input
            type="file"
            accept=".csv"
            onChange={handleFileChange}
            className="hidden"
            id="csv-upload"
          />
          <label htmlFor="csv-upload">
            <Button asChild variant="outline" className="cursor-pointer">
              <span>{file ? file.name : "Choose File"}</span>
            </Button>
          </label>
          <div className="mt-8">
            <Button
              className="bg-button-coloured px-8"
              disabled={!file || loading}
              onClick={handleUploadPreview}
            >
              {loading ? "Parsing..." : "Next: Map Categories"}
            </Button>
          </div>
        </div>
      ) : (
        <div className="bg-card border border-border rounded-xl overflow-hidden shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left">
              <thead className="bg-muted text-muted-foreground font-medium border-b">
                <tr>
                  <th className="px-4 py-4 min-w-[200px]">Equipment Name</th>
                  <th className="px-4 py-4 min-w-[120px]">Brand</th>
                  <th className="px-4 py-4 min-w-[150px]">Model</th>
                  <th className="px-4 py-4 min-w-[150px]">Serial Number</th>
                  <th className="px-4 py-4 min-w-[100px]">Price</th>
                  <th className="px-4 py-4 min-w-[100px]">Daily Rate</th>
                  <th className="px-4 py-4 min-w-[100px]">Location</th>
                  <th className="px-4 py-4 min-w-[250px] sticky right-0 bg-muted shadow-[-5px_0px_10px_-5px_rgba(0,0,0,0.1)]">
                    Select Category
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {previewData.map((item, idx) => (
                  <tr key={idx} className="hover:bg-muted/50 transition-colors">
                    <td className="px-4 py-3 font-semibold text-foreground">
                      {item.equipment_name}
                    </td>
                    <td className="px-4 py-3">{item.manufacturer}</td>
                    <td className="px-4 py-3">{item.model_number}</td>
                    <td className="px-4 py-3 text-xs font-mono">
                      {item.serial_number || "N/A"}
                    </td>
                    <td className="px-4 py-3">${item.purchase_price}</td>
                    <td className="px-4 py-3 font-medium text-green-600 dark:text-green-400">
                      ${item.daily_rental_rate}
                    </td>
                    <td className="px-4 py-3">{item.storage_location}</td>
                    <td className="px-4 py-3 sticky right-0 bg-card shadow-[-5px_0px_10px_-5px_rgba(0,0,0,0.1)]">
                      <Select
                        value={item.category_id ? String(item.category_id) : ""}
                        onValueChange={(val) => handleCategoryChange(idx, val)}
                      >
                        <SelectTrigger className="h-10 w-full">
                          <SelectValue placeholder="Choose Category" />
                        </SelectTrigger>

                        <SelectContent
                          position="popper"
                          sideOffset={4}
                          className="z-[100] min-w-[200px] [&>div]:!h-auto [&>div]:!max-h-[300px]"
                        >
                          {categories.length > 0 ? (
                            categories.map((cat) => (
                              <SelectItem
                                key={cat.category_id}
                                value={String(cat.category_id)}
                              >
                                {cat.name}
                              </SelectItem>
                            ))
                          ) : (
                            <div className="p-4 text-center text-xs text-muted-foreground">
                              No categories found
                            </div>
                          )}
                        </SelectContent>
                      </Select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="p-4 border-t border-border flex justify-between items-center bg-muted/20">
            <span className="text-sm text-muted-foreground">
              {previewData.length} items ready to import
            </span>
            <div className="flex gap-3">
              <Button variant="outline" onClick={() => setStep(1)}>
                Change File
              </Button>
              <Button
                className="bg-button-coloured"
                onClick={handleFinalSave}
                disabled={loading}
              >
                {loading ? "Saving..." : <><Check className="mr-2 h-4 w-4" /> Confirm & Save All</>}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BulkUploadEquipment;
